import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Divider,
  Alert,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Avatar,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  ListItemIcon,
  Fab,
  Pagination,
  Collapse,
  Stack,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  TableContainer,
  TablePagination,
  InputAdornment,
  CircularProgress
} from '@mui/material';

import {
  Scanner as ScannerIcon,
  Settings as SettingsIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PlayArrow as TestIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  Science as ScienceIcon,
  Biotech as BiotechIcon,
  LocalHospital as HospitalIcon,
  Close as CloseIcon,
  Category as CategoryIcon,
  Tune as TuneIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  Visibility as VisibilityIcon,
  Search as SearchIcon,
  AutoAwesome as AutoAwesomeIcon,
  InfoOutlined as InfoIcon,
  PriceChange as PriceChangeIcon,
  AttachMoney as MoneyIcon,
} from '@mui/icons-material';

import { styled } from '@mui/material/styles';
import {
  loadAllScansWithParameters, addScanCategory, updateScanCategory, updateScanFormat,
  deleteScanCategory, addScanParameter, updateScanParameter,
  deleteScanParameter, generateScanParameters, updateScanWaitingTime,
  getScanInsurances, loadInsurance,
  facilitySettingsAPI, scanPriceChangeRequestCreate,
} from '../BaseURL';
import { generateScanReportHTMLDekyi } from './ScanReportTemplateDekyi';

const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[8],
  },
}));

const StyledAccordion = styled(Accordion)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  borderRadius: theme.spacing(1),
  '&:before': {
    display: 'none',
  },
  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
}));

const SetupScan = () => {
  const [labCategories, setLabCategories] = useState([]);
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedParameter, setSelectedParameter] = useState(null);
  const [isCategoryDialogOpen, setIsCategoryDialogOpen] = useState(false);
  const [isParameterDialogOpen, setIsParameterDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState('add'); 
  const [categoryFormData, setCategoryFormData] = useState({
    name: '',
    description: '',
    // Days the scan is expected to take (matches lab_cat.waiting_time).
    // Stored on scan_price.waiting_time per the 020 migration.
    waitingTime: 1,
    // Report format — 'parameter' (default; every existing scan) or
    // 'narrative' (per migration 040). Narrative flips the result entry
    // to per-section text areas + a clinical Impression, and the report
    // template renders the descriptive layout instead of the parameter
    // table. Sent to the backend on save.
    scanFormat: 'parameter',
  });
  const [parameterFormData, setParameterFormData] = useState({
    name: '',
    description: ''
  });
  // Each parametersList row carries:
  //   id              — local row id (stable for React lists)
  //   dbId            — scan_reg.id once persisted, undefined for new rows
  //   name            — parameter name (paramater column)
  //   description     — flag list (Flag column, comma-separated)
  //   displayOrder    — print/entry order, lower = first
  //   defaultFinding  — pre-fill text for the entry dialog
  //   valueType       — 'text' (narrative) or 'numeric' (uses range parser)
  //   unit            — display unit when valueType === 'numeric'
  //   refRange        — reference range when valueType === 'numeric'
  const makeBlankParamRow = () => ({
    id: Date.now() + Math.random(),
    name: '',
    description: '',
    // Filled in by renumberRows the moment the row joins the list — the
    // array position, not this field, decides the order.
    displayOrder: '',
    defaultFinding: '',
    valueType: 'text',
    unit: '',
    refRange: '',
  });

  // The array position IS the display order. Every mutation that can
  // change positions (add / remove / arrows / typing a new number / an AI
  // append) runs the list back through this, so the badge, the per-row "#"
  // input and the value written to scan_reg.display_order can never drift
  // apart — moving a row renumbers it immediately, ready to save.
  const renumberRows = (rows) => rows.map((p, idx) => ({ ...p, displayOrder: idx + 1 }));

  const [parametersList, setParametersList] = useState(() => renumberRows([makeBlankParamRow()]));
  const [loading, setLoading] = useState(false);
  const [aiDialogOpen, setAiDialogOpen] = useState(false);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiError, setAiError] = useState('');
  const [aiForm, setAiForm] = useState({
    scanName: '',
    modality: '',
    bodyRegion: '',
    region: 'Ghana / West Africa',
    extraNotes: '',
  });
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  // Table pagination — zero-based to match MUI's TablePagination API.
  const [page, setPage] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // ── Scan pricing info + change-request state (mirror of Home.js) ─────
  // Click the info icon next to a scan → see every insurance partner's
  // tariff (read-only). The "Request change" button files a request
  // that admins review and approve from their queue.
  const [infoDialogOpen, setInfoDialogOpen] = useState(false);
  const [infoScanName,   setInfoScanName]   = useState('');
  const [infoRows,       setInfoRows]       = useState([]);
  const [infoLoading,    setInfoLoading]    = useState(false);
  const [insurancePartners, setInsurancePartners] = useState([]);
  const [scanPriceInfoEnabled, setScanPriceInfoEnabled] = useState(true);

  const [reqOpen,      setReqOpen]      = useState(false);
  const [reqInsurance, setReqInsurance] = useState('');
  const [reqCurrent,   setReqCurrent]   = useState(null);
  const [reqAmount,    setReqAmount]    = useState('');
  const [reqReason,    setReqReason]    = useState('');
  const [reqSaving,    setReqSaving]    = useState(false);

  // ── Report preview state ────────────────────────────────────────────
  // Click the eye icon on any scan row → build a mock report with the
  // current parameters (in the order the admin set with the arrows)
  // using their configured default findings, and render it through the
  // Dekyi scan template. Lets the admin see exactly how the printout
  // will look before saving or requesting.
  const [previewOpen,     setPreviewOpen]     = useState(false);
  const [previewCategory, setPreviewCategory] = useState(null);
  const [previewHtml,     setPreviewHtml]     = useState('');

  const currentUserLabel = (() => {
    try {
      const u = JSON.parse(localStorage.getItem('userInfo') || '{}');
      return u?.name || u?.username || '';
    } catch (_) { return ''; }
  })();

  // Read the facility toggle once at mount. The lab and scan info
  // icons share one toggle (`lab_price_info_enabled`) so admin only
  // has one switch to manage.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(facilitySettingsAPI, { timeout: 8000 });
        if (!alive) return;
        const row = data?.success && Array.isArray(data.results)
          ? data.results.find((r) => r.key === 'lab_price_info_enabled')
          : null;
        if (row) {
          const s = String(row.value ?? '').trim().toLowerCase();
          setScanPriceInfoEnabled(!(s === 'false' || s === '0' || s === 'off' || s === 'no'));
        }
      } catch (_) { /* keep default ON */ }
    })();
    return () => { alive = false; };
  }, []);

  const loadInsurancePartnersOnce = async () => {
    if (insurancePartners.length > 0) return;
    try {
      const res = await axios.get(loadInsurance);
      if (res.data?.success) setInsurancePartners(res.data.results || []);
    } catch (_) { /* dropdown stays empty */ }
  };

  const openScanInfo = async (scanName) => {
    setInfoScanName(scanName);
    setInfoRows([]);
    setInfoDialogOpen(true);
    setInfoLoading(true);
    loadInsurancePartnersOnce();
    try {
      const res = await axios.post(getScanInsurances, { scanName });
      setInfoRows(res.data?.success ? (res.data.results || []) : []);
    } catch (_) {
      setInfoRows([]);
    } finally {
      setInfoLoading(false);
    }
  };

  const openRequestChange = (row = null) => {
    setReqInsurance(row?.status || '');
    setReqCurrent(row && Number.isFinite(Number(row.tariff)) ? Number(row.tariff) : null);
    setReqAmount(row?.tariff != null ? String(row.tariff) : '');
    setReqReason('');
    setReqOpen(true);
  };

  const submitChangeRequest = async () => {
    if (!infoScanName) return;
    const amt = Number(reqAmount);
    if (!Number.isFinite(amt) || amt < 0) {
      await Swal.fire({
        icon: 'warning',
        title: 'Enter a valid amount',
        text: 'Proposed amount must be a non-negative number.',
      });
      return;
    }
    setReqSaving(true);
    try {
      const { data } = await axios.post(scanPriceChangeRequestCreate, {
        scan:             infoScanName,
        insurance:        reqInsurance || null,
        currentTariff:    reqCurrent,
        requestedTariff:  amt,
        reason:           reqReason || null,
        requestedBy:      currentUserLabel || null,
      });
      if (data?.success) {
        setReqOpen(false);
        await Swal.fire({
          icon: 'success',
          title: 'Request sent',
          text: 'Your scan price-change request has been logged for admin review.',
          timer: 2200, showConfirmButton: false,
        });
      } else {
        await Swal.fire({ icon: 'error', title: 'Could not submit', text: data?.message || 'Try again.' });
      }
    } catch (err) {
      await Swal.fire({ icon: 'error', title: 'Could not submit', text: err.response?.data?.message || err.message });
    } finally {
      setReqSaving(false);
    }
  };

  // Load scan data from API
  useEffect(() => {
    const loadScanData = async () => {
      setLoading(true);
      try {
        const response = await axios.get(loadAllScansWithParameters());
        if (response.data && response.data.success) {
          // Transform the data to match the expected format. Pull through
          // every metadata field added in migration 021 so the table view
          // can show them and the dialogs can edit them.
          const transformedCategories = response.data.data.map((scan, index) => ({
            id: index + 1,
            name: scan.scan,
            description: scan.scan,
            waitingTime: scan.waiting_time ?? 1,
            // Backend returns this per scan (migration 040); default to
            // 'parameter' for legacy rows that predate the column.
            scanFormat: scan.scan_format === 'narrative' ? 'narrative' : 'parameter',
            parameters: scan.parameters ? scan.parameters.map((param) => ({
              id: param.id,
              name: param.paramater,
              description: param.Flag || '-',
              displayOrder:    param.display_order   ?? 0,
              defaultFinding:  param.default_finding ?? '',
              valueType:       param.value_type      || 'text',
              unit:            param.unit            ?? '',
              refRange:        param.ref_range       ?? '',
            })) : []
          }));
          setLabCategories(transformedCategories);
          setFilteredCategories(transformedCategories);
        } else {
          console.error('Failed to load scan data:', response.data?.message);
          setLabCategories([]);
          setFilteredCategories([]);
        }
      } catch (error) {
        console.error('Error loading scan data:', error);
        setLabCategories([]);
        setFilteredCategories([]);
      } finally {
        setLoading(false);
      }
    };

    loadScanData();
  }, []);

  // Filter categories based on search term
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredCategories(labCategories);
    } else {
      const filtered = labCategories.filter(category => {
        const q = searchTerm.toLowerCase();
        const categoryMatch =
          (category.name || '').toLowerCase().includes(q) ||
          (category.description || '').toLowerCase().includes(q);

        // Search parameters by name + default finding. `description`
        // (the retired flag list) is no longer part of the setup form,
        // so it's excluded from search — matches would surface data
        // the user can't see anywhere else.
        const parameterMatch = category.parameters.some(param =>
          (param.name || '').toLowerCase().includes(q) ||
          (param.default_finding || param.defaultFinding || '').toLowerCase().includes(q)
        );

        return categoryMatch || parameterMatch;
      });
      setFilteredCategories(filtered);
    }
    setPage(0); // Reset to first page when filtering changes
  }, [searchTerm, labCategories]);

  const handleCategoryDialogOpen = (mode, category = null) => {
    setDialogMode(mode);
    if (mode === 'edit' && category) {
      setCategoryFormData({
        name: category.name,
        description: category.description,
        waitingTime: category.waitingTime ?? 1,
        scanFormat: category.scanFormat === 'narrative' ? 'narrative' : 'parameter',
      });
      setSelectedCategory(category);
    } else {
      setCategoryFormData({
        name: '',
        description: '',
        waitingTime: 1,
        scanFormat: 'parameter',
      });
      setSelectedCategory(null);
      // Reset the parameter rows so Add Scan starts clean each time.
      setParametersList(renumberRows([makeBlankParamRow()]));
    }
    setIsCategoryDialogOpen(true);
  };

  const handleCategoryDialogClose = () => {
    setIsCategoryDialogOpen(false);
    setSelectedCategory(null);
    setCategoryFormData({
      name: '',
      description: '',
      waitingTime: 1,
      scanFormat: 'parameter',
    });
    setParametersList(renumberRows([makeBlankParamRow()]));
  };

  const handleParameterDialogOpen = (mode, categoryId, parameter = null) => {
    setDialogMode(mode);
    const category = labCategories.find(cat => cat.id === categoryId);
    setSelectedCategory({ id: categoryId, name: category?.name });
    setSelectedParameter(parameter);
    if (mode === 'edit' && parameter) {
      setParametersList([{
        id: Date.now(),
        dbId: parameter.id,
        name: parameter.name,
        description: parameter.description,
        displayOrder:    parameter.displayOrder   ?? 0,
        defaultFinding:  parameter.defaultFinding ?? '',
        valueType:       parameter.valueType      || 'text',
        unit:            parameter.unit           ?? '',
        refRange:        parameter.refRange       ?? '',
      }]);
    } else {
      setParametersList(renumberRows([makeBlankParamRow()]));
    }
    setIsParameterDialogOpen(true);
  };

  // Open the parameter editor in "manage" mode for a whole scan:
  // every existing parameter is loaded as a row (carrying its DB id),
  // the user can add more rows or use the AI to append, and on save we
  // diff against the original set to update / insert / delete in bulk.
  const handleOpenManageDialog = (category) => {
    if (!category) return;
    setDialogMode('manage');
    setSelectedCategory({ id: category.id, name: category.name });
    setSelectedParameter(null);
    // Load in stored display_order so the rows open in the same sequence
    // the technician and the printed report see, then renumber to a clean
    // 1..n — gaps or duplicates left by older data would otherwise make
    // the "#" input disagree with the row's actual position.
    const orderOf = (p) => {
      const n = parseInt(p?.displayOrder, 10);
      return Number.isFinite(n) ? n : Number.MAX_SAFE_INTEGER;
    };
    const rows = [...(category.parameters || [])]
      .map((p, idx) => ({ p, idx }))
      .sort((a, b) => (orderOf(a.p) - orderOf(b.p)) || (a.idx - b.idx))
      .map(({ p }, idx) => ({
        id: Date.now() + idx,
        dbId: p.id,
        name: p.name || '',
        description: (p.description && p.description !== '-') ? p.description : '',
        displayOrder:    p.displayOrder   ?? idx,
        defaultFinding:  p.defaultFinding ?? '',
        valueType:       p.valueType      || 'text',
        unit:            p.unit           ?? '',
        refRange:        p.refRange       ?? '',
      }));
    setParametersList(renumberRows(rows.length > 0 ? rows : [makeBlankParamRow()]));
    setIsParameterDialogOpen(true);
  };

  const handleParameterDialogClose = () => {
    setIsParameterDialogOpen(false);
    setSelectedCategory(null);
    setSelectedParameter(null);
    setParametersList(renumberRows([makeBlankParamRow()]));
  };

  const handleAddParameterField = () => {
    setParametersList(prev => renumberRows([...prev, makeBlankParamRow()]));
  };

  const handleRemoveParameterField = (id) => {
    setParametersList(prev => {
      const next = prev.filter(param => param.id !== id);
      // Keep the form interactive by leaving a single blank row when the
      // user clears the last parameter. Everything below the removed row
      // closes the gap so the numbering stays 1..n.
      return renumberRows(next.length > 0 ? next : [makeBlankParamRow()]);
    });
  };

  const handleParameterChange = (id, field, value) => {
    setParametersList(parametersList.map(param =>
      param.id === id ? { ...param, [field]: value } : param
    ));
  };

  // ─── Reorder helpers ─────────────────────────────────────────────────
  // The array index is the source of truth for scan-parameter order —
  // each move renumbers the whole list through renumberRows, so the "#"
  // input and badge update on the spot and the save loop writes the same
  // sequence to scan_reg.display_order. Result entry and the printed
  // report read that column back out, so one move-up/move-down flows
  // through everywhere. Mirrors moveParameterUp/Down in Home.js so the
  // two setup screens behave identically.
  const moveScanParameterUp = (parameterId) => {
    setParametersList((prev) => {
      const idx = prev.findIndex((p) => p.id === parameterId);
      if (idx <= 0) return prev;
      const next = [...prev];
      [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
      return renumberRows(next);
    });
  };
  const moveScanParameterDown = (parameterId) => {
    setParametersList((prev) => {
      const idx = prev.findIndex((p) => p.id === parameterId);
      if (idx === -1 || idx >= prev.length - 1) return prev;
      const next = [...prev];
      [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
      return renumberRows(next);
    });
  };

  // The "#" input is a jump-to-position shortcut for long lists: typing a
  // number and leaving the field (blur or Enter) physically moves the row
  // to that position, then renumbers everything. A blank or out-of-range
  // entry snaps back to the row's real position instead of leaving a hole
  // in the sequence.
  const commitParameterOrder = (parameterId) => {
    setParametersList((prev) => {
      const idx = prev.findIndex((p) => p.id === parameterId);
      if (idx === -1) return prev;
      const typed = parseInt(prev[idx].displayOrder, 10);
      if (!Number.isFinite(typed)) return renumberRows(prev);
      const target = Math.min(Math.max(typed, 1), prev.length) - 1;
      if (target === idx) return renumberRows(prev);
      const next = [...prev];
      const [row] = next.splice(idx, 1);
      next.splice(target, 0, row);
      return renumberRows(next);
    });
  };

  const handleOpenAiDialog = (fromDialog = false) => {
    // Pre-fill the scan name from whichever editor is open:
    // - Add Scan dialog (uses categoryFormData.name)
    // - Manage / Add Parameter dialog (uses selectedCategory.name)
    const preFilled = fromDialog
      ? (categoryFormData.name || selectedCategory?.name || '')
      : '';
    setAiForm({
      scanName: preFilled,
      modality: '',
      bodyRegion: '',
      region: 'Ghana / West Africa',
      extraNotes: '',
    });
    setAiError('');
    setAiDialogOpen(true);
  };

  const handleCloseAiDialog = () => {
    if (aiGenerating) return;
    setAiDialogOpen(false);
    setAiError('');
  };

  const reloadScans = async () => {
    const loadResponse = await axios.get(loadAllScansWithParameters());
    if (loadResponse.data && loadResponse.data.success) {
      const transformedCategories = loadResponse.data.data.map((scan, index) => ({
        id: index + 1,
        name: scan.scan,
        description: scan.scan,
        waitingTime: scan.waiting_time ?? 1,
        parameters: scan.parameters ? scan.parameters.map((param) => ({
          id: param.id,
          name: param.paramater,
          description: param.Flag || '-',
          displayOrder:    param.display_order   ?? 0,
          defaultFinding:  param.default_finding ?? '',
          valueType:       param.value_type      || 'text',
          unit:            param.unit            ?? '',
          refRange:        param.ref_range       ?? '',
        })) : []
      }));
      setLabCategories(transformedCategories);
      setFilteredCategories(transformedCategories);
    }
  };

  const handleGenerateWithAi = async () => {
    const scanName = (aiForm.scanName || selectedCategory?.name || '').trim();
    if (!scanName) {
      setAiError('Enter a scan name first.');
      return;
    }
    setAiGenerating(true);
    setAiError('');
    try {
      const { data } = await axios.post(generateScanParameters, {
        scanName,
        modality:   aiForm.modality,
        bodyRegion: aiForm.bodyRegion,
        region:     aiForm.region,
        extraNotes: aiForm.extraNotes,
      });
      if (!data?.success) throw new Error(data?.message || 'AI generation failed');
      const params = Array.isArray(data.data?.parameters) ? data.data.parameters : [];
      if (params.length === 0) {
        setAiError('The AI did not return any parameters. Try adding modality or body region.');
        return;
      }
      const rows = params.map((p, idx) => ({
        id: Date.now() + idx,
        name: p.name || '',
        description: p.description || 'normal, abnormal',
        displayOrder: idx,
        defaultFinding: '',
        valueType: 'text',
        unit: '',
        refRange: '',
      }));

      // If invoked from the Add Scan dialog, also keep the scan name in
      // sync (so the user doesn't have to retype it after closing the AI
      // form) and populate the parameter rows for review/edit.
      if (isCategoryDialogOpen) {
        setCategoryFormData(prev => ({ ...prev, name: scanName }));
      }

      // Merge into whatever rows the user already has — drop blank starter
      // rows and skip duplicates by name (case-insensitive).
      const existingNames = new Set(
        parametersList.map(r => r.name.trim().toLowerCase()).filter(Boolean)
      );
      const newRows = rows.filter(r => !existingNames.has(r.name.trim().toLowerCase()));
      const keptExisting = parametersList.filter(r => r.name.trim() !== '');
      const merged = [...keptExisting, ...newRows];
      // Appended rows continue the existing sequence rather than carrying
      // the AI's own 0-based numbering into the list.
      setParametersList(renumberRows(merged.length > 0 ? merged : rows));
      setAiDialogOpen(false);
    } catch (err) {
      const msg = err?.response?.data?.message || err.message || 'Failed to generate parameters';
      setAiError(msg);
    } finally {
      setAiGenerating(false);
    }
  };

  const handleSaveCategory = async () => {
    setLoading(true);
    Swal.fire({
      title: dialogMode === 'add' ? 'Saving Scan…' : 'Updating Scan…',
      text: dialogMode === 'add'
        ? 'Please wait while we save your scan and parameters'
        : 'Please wait while we update this scan',
      allowOutsideClick: false,
      showConfirmButton: false,
      willOpen: () => Swal.showLoading(),
    });
    try {
      if (dialogMode === 'add') {
        const scanName = categoryFormData.name.trim();
        const waitingTime = parseInt(categoryFormData.waitingTime, 10);
        const safeWait = Number.isFinite(waitingTime) && waitingTime >= 0 ? waitingTime : 1;
        const response = await axios.post(addScanCategory, {
          scanName,
          waitingTime: safeWait,
        });

        if (response.data.success) {
          // Push waiting_time onto scan_price rows for this scan in case
          // the addScanCategory endpoint didn't find any matching rows yet.
          // The endpoint accepts the param but it's idempotent, so calling
          // updateScanWaitingTime explicitly keeps the two flows aligned.
          try {
            await axios.put(updateScanWaitingTime(scanName), { waitingTime: safeWait });
          } catch (_) { /* silently ignore — non-fatal */ }

          // Persist the report-format pick from the dialog. addScanCategory
          // inserts the anchor row with the default 'parameter' format;
          // this flips every row for the scan (there's only the anchor
          // at this point, but any parameters inserted next also read
          // through the same UPDATE) to narrative when the user asked
          // for it.
          if (categoryFormData.scanFormat === 'narrative') {
            try {
              await axios.put(updateScanFormat(scanName), { scanFormat: 'narrative' });
            } catch (_) { /* silently ignore — non-fatal */ }
          }

          // Persist any parameter rows the user filled (manually or via AI)
          // in the same dialog. Blank rows are ignored.
          const validParameters = parametersList.filter(p => p.name.trim() !== '');
          if (validParameters.length > 0) {
            await Promise.all(
              validParameters.map((p, idx) => axios.post(addScanParameter, {
                scanName,
                parameterName: p.name.trim(),
                // Scans are narrative-only — flags are no longer part
                // of the setup. Send a placeholder so the (existing)
                // column stays populated but doesn't drive any UI.
                flag: '-',
                // The row's position in the list the admin arranged with
                // the arrows / "#" input.
                displayOrder:    idx + 1,
                defaultFinding:  p.defaultFinding || '',
                valueType:       p.valueType || 'text',
                unit:            p.unit || '',
                refRange:        p.refRange || '',
              }))
            );
          }
          await reloadScans();
          handleCategoryDialogClose();
          await Swal.fire({
            title: 'Success!',
            text: validParameters.length > 0
              ? `Scan "${scanName}" saved with ${validParameters.length} parameter${validParameters.length === 1 ? '' : 's'}.`
              : `Scan "${scanName}" saved.`,
            icon: 'success',
            confirmButtonColor: '#11998e',
            confirmButtonText: 'Great!',
            timer: 2000,
            timerProgressBar: true,
          });
          return;
        } else {
          await Swal.fire({
            title: 'Error',
            text: response.data.message || 'Failed to save scan',
            icon: 'error',
            confirmButtonColor: '#d33',
            confirmButtonText: 'OK',
          });
        }
      } else if (dialogMode === 'edit' && selectedCategory) {
        const response = await axios.put(updateScanCategory(selectedCategory.name), {
          newScanName: categoryFormData.name,
          scanFormat:  categoryFormData.scanFormat === 'narrative' ? 'narrative' : 'parameter',
        });

        if (response.data.success) {
          // Persist the waiting time alongside the rename. We address the
          // updated name because the rename above already propagated.
          try {
            const wait = parseInt(categoryFormData.waitingTime, 10);
            await axios.put(updateScanWaitingTime(categoryFormData.name), {
              waitingTime: Number.isFinite(wait) && wait >= 0 ? wait : 1,
            });
          } catch (_) { /* silently ignore — non-fatal */ }

          await reloadScans();
          handleCategoryDialogClose();
          await Swal.fire({
            title: 'Success!',
            text: `Scan renamed to "${categoryFormData.name}".`,
            icon: 'success',
            confirmButtonColor: '#11998e',
            confirmButtonText: 'Great!',
            timer: 2000,
            timerProgressBar: true,
          });
          return;
        } else {
          await Swal.fire({
            title: 'Error',
            text: response.data.message || 'Failed to update scan',
            icon: 'error',
            confirmButtonColor: '#d33',
            confirmButtonText: 'OK',
          });
        }
      }
      handleCategoryDialogClose();
    } catch (error) {
      console.error('Error saving scan:', error);
      await Swal.fire({
        title: 'Error',
        text: error?.response?.data?.message || error.message || 'Failed to save scan',
        icon: 'error',
        confirmButtonColor: '#d33',
        confirmButtonText: 'OK',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveParameter = async () => {
    // Validate that all parameters have names
    const validParameters = parametersList.filter(param => param.name.trim() !== '');
    if (validParameters.length === 0) return;

    setLoading(true);
    Swal.fire({
      title: 'Saving Parameters…',
      text: 'Please wait while we save your scan parameters',
      allowOutsideClick: false,
      showConfirmButton: false,
      willOpen: () => Swal.showLoading(),
    });

    // Track the bulk counts so the success notification can mirror the
    // lab's "Successfully saved (X updated, Y created, Z deleted)" line.
    let updatedCount = 0;
    let createdCount = 0;
    let deletedCount = 0;

    // Helper — builds the per-parameter payload so add / update / manage
    // all post the same metadata fields the backend now stores.
    // Scans are narrative-only, so `flag` is fixed to '-'. `order` is the
    // 1-based position of the row in the list the admin just arranged;
    // the arrows and the "#" input both keep that position authoritative,
    // so there's no stale typed number here to second-guess.
    const buildPayload = (p, order) => ({
      parameterName: p.name,
      flag: '-',
      displayOrder:    order,
      defaultFinding:  p.defaultFinding || '',
      valueType:       p.valueType || 'text',
      unit:            p.unit || '',
      refRange:        p.refRange || '',
    });

    try {
      if (dialogMode === 'add') {
        const savePromises = validParameters.map((param, idx) =>
          axios.post(addScanParameter, {
            scanName: selectedCategory.name,
            ...buildPayload(param, idx + 1),
          })
        );
        await Promise.all(savePromises);
        createdCount = validParameters.length;
      } else if (dialogMode === 'manage' && selectedCategory) {
        // Diff against the original set so the bulk save covers updates,
        // inserts and deletes in one go.
        const originalCategory = labCategories.find(c => c.id === selectedCategory.id);
        const originalIds = new Set(
          (originalCategory?.parameters || []).map(p => p.id)
        );
        const keptIds = new Set(
          validParameters.filter(p => p.dbId).map(p => p.dbId)
        );

        // Tag each row with its final 1-based display position BEFORE
        // splitting into update / insert buckets — otherwise the buckets
        // number themselves independently and the order the user set with
        // the arrow buttons doesn't survive.
        const ordered  = validParameters.map((p, idx) => ({ ...p, __order: idx + 1 }));
        const toUpdate = ordered.filter(p => p.dbId);
        const toInsert = ordered.filter(p => !p.dbId);
        const toDelete = Array.from(originalIds).filter(id => !keptIds.has(id));

        await Promise.all([
          ...toUpdate.map((p) => axios.put(updateScanParameter(p.dbId), buildPayload(p, p.__order))),
          ...toInsert.map((p) => axios.post(addScanParameter, {
            scanName: selectedCategory.name,
            ...buildPayload(p, p.__order),
          })),
          ...toDelete.map(id => axios.delete(deleteScanParameter(id))),
        ]);

        updatedCount = toUpdate.length;
        createdCount = toInsert.length;
        deletedCount = toDelete.length;
      } else if (dialogMode === 'edit' && selectedParameter && validParameters.length === 1) {
        // Editing one parameter in isolation — there's no list to reorder
        // here, so keep the display_order it already has instead of
        // resetting it to the top.
        const storedOrder = parseInt(selectedParameter.displayOrder, 10);
        const response = await axios.put(
          updateScanParameter(selectedParameter.id),
          buildPayload(
            validParameters[0],
            Number.isFinite(storedOrder) && storedOrder > 0 ? storedOrder : 1
          )
        );

        if (!response.data.success) {
          throw new Error(response.data.message || 'Failed to update parameter');
        }
        updatedCount = 1;
      }

      await reloadScans();
      handleParameterDialogClose();

      // Build the same kind of summary message the lab uses.
      const parts = [];
      if (updatedCount) parts.push(`${updatedCount} updated`);
      if (createdCount) parts.push(`${createdCount} created`);
      if (deletedCount) parts.push(`${deletedCount} deleted`);
      const summary = parts.length > 0 ? `(${parts.join(', ')})` : '';

      await Swal.fire({
        title: 'Success!',
        text: `Parameters saved successfully ${summary}`.trim() + '.',
        icon: 'success',
        confirmButtonColor: '#11998e',
        confirmButtonText: 'Great!',
        timer: 2000,
        timerProgressBar: true,
      });
    } catch (error) {
      console.error('Error saving parameters:', error);
      await Swal.fire({
        title: 'Error',
        text: error?.response?.data?.message || error.message || 'Failed to save parameters',
        icon: 'error',
        confirmButtonColor: '#d33',
        confirmButtonText: 'OK',
      });
    } finally {
      setLoading(false);
    }
  };

  // ── Report-preview helpers ──────────────────────────────────────────
  // Build a mock reportData payload keyed by today's date and the scan
  // name so the ScanReportTemplateDekyi renderer can turn a plain array
  // of parameters into a full styled preview. Each row's `finding` comes
  // from the configured `default_finding` when set, otherwise a friendly
  // placeholder so the layout is still populated end-to-end.
  const buildDummyScanReportData = (params, scanName) => {
    if (!Array.isArray(params) || params.length === 0 || !scanName) return {};
    // Honour the display order the admin set with the arrows / manual
    // reorder in the dialog. category.parameters comes back sorted
    // server-side already; parametersList is sorted by array index by
    // construction.
    const ordered = [...params]
      .filter((p) => (p.name || p.paramater) && String(p.name || p.paramater).trim())
      .map((p) => {
        const paramName = p.name || p.paramater || '';
        const finding = (p.default_finding || p.defaultFinding || '').trim();
        return {
          para:    paramName,
          finding: finding || `Sample finding for ${paramName}. Replace with the tech's actual observation at result entry.`,
          flag:    'normal',
        };
      });
    const today = new Date().toISOString().slice(0, 10);
    return { [today]: { [scanName]: ordered } };
  };

  const renderScanPreviewHtml = (params, scanName) => {
    if (!scanName || !Array.isArray(params) || params.length === 0) return '';
    const reportData = buildDummyScanReportData(params, scanName);
    const dummyPatient = {
      id:       'PREVIEW-0001',
      fullName: 'Sample Patient',
      age:      30,
      gender:   'Male',
    };
    const dummyUser = { name: 'Preview Sonographer', username: 'preview' };
    return generateScanReportHTMLDekyi(
      reportData,
      dummyPatient,
      dummyUser,
      null,
      {
        verifiedBy:      'Sample Radiologist',
        verifiedTitle:   'Radiologist',
        approvedBy:      'Sample Consultant',
        approvedTitle:   'Senior Radiologist / Consultant',
        referringDoctor: 'Dr. Sample',
        modality:        'Ultrasound',
      }
    );
  };

  // Opens the preview dialog for a saved category (uses whatever's on
  // the server via `category.parameters`).
  const handleOpenScanPreview = (category) => {
    const params = Array.isArray(category?.parameters) ? category.parameters : [];
    setPreviewCategory({ name: category?.name || '(unnamed scan)', parameters: params });
    setPreviewHtml(renderScanPreviewHtml(params, category?.name || 'Scan'));
    setPreviewOpen(true);
  };

  // Opens the preview from inside the parameter dialog using the live
  // `parametersList` so the admin can eye-check ordering + default
  // findings before saving. Filters out blank rows so a half-filled
  // editor doesn't produce a section header with no rows underneath.
  const handleOpenScanPreviewFromDialog = () => {
    const scanName = selectedCategory?.name
      || categoryFormData?.name
      || 'Scan';
    const params = (parametersList || [])
      .filter((p) => (p.name || '').trim())
      .map((p) => ({
        name:            p.name,
        default_finding: p.defaultFinding || '',
      }));
    if (params.length === 0) {
      Swal.fire({
        icon: 'info',
        title: 'Nothing to preview yet',
        text:  'Add at least one parameter before previewing the report.',
        confirmButtonColor: '#1A9B7F',
      });
      return;
    }
    setPreviewCategory({ name: scanName, parameters: params });
    setPreviewHtml(renderScanPreviewHtml(params, scanName));
    setPreviewOpen(true);
  };

  const handleCloseScanPreview = () => {
    setPreviewOpen(false);
    setPreviewCategory(null);
    setPreviewHtml('');
  };

  const handleOpenScanPreviewInWindow = () => {
    if (!previewHtml) return;
    const w = window.open('', '_blank');
    if (!w) {
      Swal.fire({
        icon: 'warning',
        title: 'Pop-ups blocked',
        text:  'Allow pop-ups for this site to open the preview in a new window.',
        confirmButtonColor: '#1A9B7F',
      });
      return;
    }
    w.document.write(previewHtml);
    w.document.close();
  };

  const handleDeleteCategory = async (categoryId) => {
    const category = labCategories.find(cat => cat.id === categoryId);
    if (!category) return;

    // Clear, branded confirmation — explicitly names everything the
    // delete will wipe so there are no surprises (parameters + all
    // per-insurance pricing rows). Patient findings and historical
    // billing records are preserved.
    const paramN = category.parameters?.length || 0;
    const confirm = await Swal.fire({
      title: `Delete "${category.name}"?`,
      html: `
        <div style="text-align:left; font-size: 0.95rem; line-height: 1.5;">
          This will permanently remove:
          <ul style="margin: 8px 0 0 20px; padding: 0;">
            <li><b>${paramN}</b> scan parameter${paramN === 1 ? '' : 's'}</li>
            <li>All <b>insurance price records</b> linked to this scan</li>
            <li>The scan category itself</li>
          </ul>
          <div style="margin-top:10px; padding: 8px 10px; background: #ecfdf5; border-left: 3px solid #10b981; border-radius: 4px; font-size: 0.85rem;">
            <b>Patient results and past billing are NOT affected.</b>
          </div>
        </div>
      `,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#9ca3af',
      reverseButtons: true,
    });
    if (!confirm.isConfirmed) return;

    try {
      const response = await axios.delete(deleteScanCategory(category.name));
      if (response.data.success) {
        await reloadScans();
        await Swal.fire({
          title: 'Deleted',
          text: response.data.message || `"${category.name}" was removed.`,
          icon: 'success',
          confirmButtonColor: '#11998e',
          confirmButtonText: 'OK',
          timer: 2400,
          timerProgressBar: true,
        });
      } else {
        Swal.fire({
          title: 'Could not delete',
          text: response.data.message || 'Failed to delete scan category.',
          icon: 'error',
          confirmButtonColor: '#dc2626',
        });
      }
    } catch (error) {
      console.error('Error deleting category:', error);
      Swal.fire({
        title: 'Error',
        text: error?.response?.data?.message || error.message || 'Failed to delete scan category.',
        icon: 'error',
        confirmButtonColor: '#dc2626',
      });
    }
  };

  const handleDeleteParameter = async (categoryId, parameterId) => {
    const category = labCategories.find(cat => cat.id === categoryId);
    const parameter = category?.parameters.find(param => param.id === parameterId);

    if (!parameter) return;

    if (window.confirm(`Are you sure you want to delete the "${parameter.name}" parameter?`)) {
      try {
        const response = await axios.delete(deleteScanParameter(parameterId));
        if (response.data.success) {
          // Reload data after successful deletion
          const loadResponse = await axios.get(loadAllScansWithParameters());
          if (loadResponse.data && loadResponse.data.success) {
            const transformedCategories = loadResponse.data.data.map((scan, index) => ({
              id: index + 1,
              name: scan.scan,
              description: scan.scan,
              enabled: scan.Flag === '1' || scan.Flag === 1,
              parameters: scan.parameters ? scan.parameters.map((param) => ({
                id: param.id,
                name: param.paramater,
                description: param.Flag || '-', // Use Flag field as description/findings
                enabled: param.Flag === '1' || param.Flag === 1
              })) : []
            }));
            setLabCategories(transformedCategories);
            setFilteredCategories(transformedCategories);
          }
        } else {
          console.error('Failed to delete parameter:', response.data.message);
        }
      } catch (error) {
        console.error('Error deleting parameter:', error);
      }
    }
  };

  const handleToggleCategory = (categoryId) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setItemsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getCategoryIcon = (categoryName) => {
    if (categoryName.includes('SOFT TISSUE')) return <BiotechIcon />;
    if (categoryName.includes('HEMATOLOGY')) return <HospitalIcon />;
    return <ScienceIcon />;
  };

  return (
    <Box sx={{ p: 3, maxWidth: '100%' }}>
      {/* Page heading */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2.5, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            width: 40, height: 40, borderRadius: 1.75,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'linear-gradient(135deg, #1A9B7F 0%, #157763 100%)',
            color: 'white', boxShadow: '0 4px 12px rgba(26,155,127,0.32)',
          }}>
            <ScannerIcon />
          </Box>
          <Box>
            <Typography sx={{ fontSize: '1.4rem', fontWeight: 700, color: 'text.primary', lineHeight: 1.15 }}>
              Setup Scan
            </Typography>
            <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary', mt: 0.25 }}>
              Configure scan types and the parameters captured for each.
            </Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon sx={{ fontSize: 18 }} />}
          onClick={() => handleCategoryDialogOpen('add')}
          sx={{
            borderRadius: 1.75, textTransform: 'none', fontWeight: 700,
            background: 'linear-gradient(135deg, #1A9B7F 0%, #157763 100%)',
            boxShadow: '0 4px 12px rgba(26,155,127,0.28)',
            '&:hover': { background: 'linear-gradient(135deg, #157763 0%, #126553 100%)', boxShadow: '0 6px 16px rgba(26,155,127,0.40)' },
          }}
        >
          Add Scan
        </Button>
      </Box>

      {/* Scans table — matches the Set Up Lab right-side pattern */}
      <Card sx={{ border: '1px solid', borderColor: 'divider', boxShadow: 'none', bgcolor: 'background.paper' }}>
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: '1rem', lineHeight: 1.2 }}>
                Scans
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {filteredCategories.length} scan{filteredCategories.length === 1 ? '' : 's'}
              </Typography>
            </Box>
            <TextField
              size="small"
              placeholder="Search scans or parameters…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ flex: 1, maxWidth: 360, '& .MuiOutlinedInput-root': { fontSize: '0.85rem', borderRadius: 1.75 } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: 'text.secondary', fontSize: 18 }} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, bgcolor: 'background.paper' }}>
              <Table size="small" sx={{
                '& .MuiTableCell-root': { py: 0.75, fontSize: '0.82rem', borderColor: 'divider' },
                '& .MuiTableCell-head': { py: 1, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '.4px', color: 'text.secondary', bgcolor: 'background.default', borderBottom: '1px solid', borderColor: 'divider' },
              }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, width: 44 }}></TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Scan</TableCell>
                    <TableCell sx={{ fontWeight: 700, width: 140 }}>Parameters</TableCell>
                    <TableCell sx={{ fontWeight: 700, width: 110, textAlign: 'right' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredCategories.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} sx={{ py: 6, textAlign: 'center', borderBottom: 'none' }}>
                        <ScannerIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                        <Typography sx={{ color: 'text.secondary', fontSize: '0.9rem' }}>
                          {searchTerm ? `No scans match "${searchTerm}"` : 'No scans configured yet. Click "Add Scan" to create one.'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : filteredCategories
                    .slice(page * itemsPerPage, page * itemsPerPage + itemsPerPage)
                    .map((category) => {
                      const isExpanded = expandedCategories.has(category.id);
                      return (
                        <React.Fragment key={category.id}>
                          <TableRow hover>
                            <TableCell>
                              <IconButton
                                size="small"
                                onClick={() => handleToggleCategory(category.id)}
                                sx={{ p: 0.25 }}
                              >
                                {isExpanded ? <KeyboardArrowUpIcon sx={{ fontSize: 18 }} /> : <KeyboardArrowDownIcon sx={{ fontSize: 18 }} />}
                              </IconButton>
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600, color: 'text.primary' }}>
                              <Stack
                                direction="row"
                                spacing={1.25}
                                alignItems="center"
                                onClick={() => handleOpenManageDialog(category)}
                                sx={{
                                  cursor: 'pointer',
                                  borderRadius: 1,
                                  px: 0.5,
                                  py: 0.25,
                                  ml: -0.5,
                                  transition: 'background-color 0.15s ease, color 0.15s ease',
                                  '&:hover': {
                                    bgcolor: 'rgba(26,155,127,0.10)',
                                    color: '#157763',
                                  },
                                }}
                              >
                                <Avatar sx={{
                                  width: 28, height: 28,
                                  bgcolor: 'rgba(26,155,127,0.18)', color: '#4ade80',
                                  border: '1px solid rgba(74,222,128,0.30)',
                                  '& .MuiSvgIcon-root': { fontSize: 16 },
                                }}>
                                  {getCategoryIcon(category.name)}
                                </Avatar>
                                <Tooltip title="Open parameter editor" arrow>
                                  <span style={{ textDecoration: 'underline', textDecorationStyle: 'dotted', textDecorationColor: 'rgba(26,155,127,0.5)', textTransform: 'uppercase' }}>
                                    {category.name}
                                  </span>
                                </Tooltip>
                              </Stack>
                            </TableCell>
                            <TableCell>
                              <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                                <Chip
                                  label={`${category.parameters.length} param${category.parameters.length === 1 ? '' : 's'}`}
                                  size="small"
                                  sx={{
                                    height: 20, fontSize: '0.7rem', fontWeight: 700,
                                    bgcolor: 'rgba(84,104,255,0.16)', color: '#7c8bff',
                                    border: '1px solid rgba(124,139,255,0.30)',
                                  }}
                                />
                                {Number.isFinite(parseInt(category.waitingTime, 10)) && (
                                  <Tooltip
                                    title={`Patient stays on the scan panel for at least ${category.waitingTime} day${category.waitingTime === 1 ? '' : 's'} and until results are entered.`}
                                    arrow
                                  >
                                    <Chip
                                      label={`${category.waitingTime}d wait`}
                                      size="small"
                                      sx={{
                                        height: 20, fontSize: '0.7rem', fontWeight: 700,
                                        bgcolor: 'rgba(245,158,11,0.16)', color: '#fbbf24',
                                        border: '1px solid rgba(251,191,36,0.30)',
                                      }}
                                    />
                                  </Tooltip>
                                )}
                              </Stack>
                            </TableCell>
                            <TableCell sx={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                              {/* Preview — mock report with the current
                                  parameters, in the order they'll print. */}
                              <Tooltip title="Preview report — see how this scan's report will look" arrow>
                                <IconButton
                                  size="small"
                                  onClick={(e) => { e.stopPropagation(); handleOpenScanPreview(category); }}
                                  sx={{ p: 0.5, color: '#5468FF' }}
                                >
                                  <VisibilityIcon sx={{ fontSize: 17 }} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Edit scan" arrow>
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleCategoryDialogOpen('edit', category)}
                                  sx={{ p: 0.5, ml: 0.25 }}
                                >
                                  <EditIcon sx={{ fontSize: 17 }} />
                                </IconButton>
                              </Tooltip>
                              {/* Info — read-only price view + request-change
                                  flow for this scan. Hidden when the facility
                                  toggle is OFF. */}
                              {scanPriceInfoEnabled && (
                                <Tooltip title="View pricing details & request changes" arrow>
                                  <IconButton
                                    size="small"
                                    onClick={() => openScanInfo(category.name)}
                                    sx={{ p: 0.5, ml: 0.25, color: '#0ea5e9' }}
                                  >
                                    <InfoIcon sx={{ fontSize: 17 }} />
                                  </IconButton>
                                </Tooltip>
                              )}
                              <Tooltip title="Delete scan" arrow>
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleDeleteCategory(category.id)}
                                  sx={{ p: 0.5, ml: 0.25 }}
                                >
                                  <DeleteIcon sx={{ fontSize: 17 }} />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={4} sx={{ bgcolor: 'background.default' }}>
                              <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                <Box sx={{ m: 1, p: 1.25, borderRadius: 1.5, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider' }}>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
                                    <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, letterSpacing: '.4px', textTransform: 'uppercase', color: 'text.secondary' }}>
                                      Parameters in {category.name}
                                    </Typography>
                                    <Button
                                      size="small"
                                      variant="text"
                                      startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                                      onClick={() => handleParameterDialogOpen('add', category.id)}
                                      sx={{ textTransform: 'none', fontWeight: 700, fontSize: '0.75rem', color: '#4ade80', '&:hover': { bgcolor: 'rgba(26,155,127,0.10)' } }}
                                    >
                                      Add Parameter
                                    </Button>
                                  </Box>

                                  {category.parameters.length === 0 ? (
                                    <Box sx={{ textAlign: 'center', py: 3 }}>
                                      <ScienceIcon sx={{ fontSize: 32, color: 'text.disabled', mb: 0.5 }} />
                                      <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 1 }}>
                                        No parameters yet
                                      </Typography>
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        startIcon={<AddIcon fontSize="small" />}
                                        onClick={() => handleParameterDialogOpen('add', category.id)}
                                        sx={{ textTransform: 'none', borderRadius: 1.5 }}
                                      >
                                        Add First
                                      </Button>
                                    </Box>
                                  ) : (
                                    <Table size="small" sx={{
                                      '& .MuiTableCell-root': { py: 0.6, fontSize: '0.78rem', borderColor: 'divider' },
                                      '& .MuiTableCell-head': { py: 0.85, fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '.4px', color: 'text.secondary', bgcolor: 'background.default', borderBottom: '1px solid', borderColor: 'divider' },
                                    }}>
                                      <TableHead>
                                        <TableRow>
                                          <TableCell sx={{ fontWeight: 700, width: 42, textAlign: 'center' }}>#</TableCell>
                                          <TableCell sx={{ fontWeight: 700 }}>Parameter</TableCell>
                                          <TableCell sx={{ fontWeight: 700 }}>Default Finding</TableCell>
                                          <TableCell sx={{ fontWeight: 700, width: 96, textAlign: 'right' }}>Actions</TableCell>
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        {/* Ordered by display_order so what
                                            the admin sees here matches the
                                            report and result-entry order. */}
                                        {[...category.parameters]
                                          .sort((a, b) =>
                                            (a.display_order ?? a.displayOrder ?? 0) -
                                            (b.display_order ?? b.displayOrder ?? 0))
                                          .map((parameter, pIdx) => (
                                            <TableRow key={parameter.id} hover>
                                              <TableCell sx={{ textAlign: 'center', color: 'text.secondary', fontWeight: 700 }}>
                                                {pIdx + 1}
                                              </TableCell>
                                              <TableCell sx={{ fontWeight: 600, color: 'text.primary' }}>
                                                <Stack direction="row" spacing={0.85} alignItems="center">
                                                  <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#1A9B7F' }} />
                                                  <span>{parameter.name}</span>
                                                </Stack>
                                              </TableCell>
                                              <TableCell sx={{ color: 'text.secondary' }}>
                                                {parameter.default_finding || parameter.defaultFinding || (
                                                  <Typography variant="caption" color="text.disabled" sx={{ fontStyle: 'italic' }}>
                                                    —
                                                  </Typography>
                                                )}
                                              </TableCell>
                                              <TableCell sx={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                                                <Tooltip title="Edit parameter" arrow>
                                                  <IconButton
                                                    size="small"
                                                    color="primary"
                                                    onClick={() => handleParameterDialogOpen('edit', category.id, parameter)}
                                                    sx={{ p: 0.5 }}
                                                  >
                                                    <EditIcon sx={{ fontSize: 16 }} />
                                                  </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Delete parameter" arrow>
                                                  <IconButton
                                                    size="small"
                                                    color="error"
                                                    onClick={() => handleDeleteParameter(category.id, parameter.id)}
                                                    sx={{ p: 0.5, ml: 0.25 }}
                                                  >
                                                    <DeleteIcon sx={{ fontSize: 16 }} />
                                                  </IconButton>
                                                </Tooltip>
                                              </TableCell>
                                            </TableRow>
                                          ))}
                                      </TableBody>
                                    </Table>
                                  )}
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        </React.Fragment>
                      );
                    })}
                </TableBody>
              </Table>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                component="div"
                count={filteredCategories.length}
                rowsPerPage={itemsPerPage}
                page={page}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
                sx={{ borderTop: '1px solid', borderColor: 'divider', '& .MuiTablePagination-toolbar': { minHeight: 44 } }}
              />
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Scan Dialog */}
      <Dialog
        open={isCategoryDialogOpen}
        onClose={handleCategoryDialogClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden',
            boxShadow: '0 20px 60px rgba(15, 23, 42, 0.18)',
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            px: 3,
            py: 2.25,
            background: 'linear-gradient(135deg, #1A9B7F 0%, #157763 100%)',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
          }}
        >
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'rgba(255,255,255,0.18)',
              backdropFilter: 'blur(8px)',
              flexShrink: 0,
            }}
          >
            <ScannerIcon sx={{ fontSize: 22 }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: '1.05rem', fontWeight: 700, lineHeight: 1.2 }}>
              {dialogMode === 'add' ? 'Add Scan' : 'Edit Scan'}
            </Typography>
            <Typography sx={{ fontSize: '0.78rem', opacity: 0.88, mt: 0.25 }}>
              {dialogMode === 'add'
                ? 'Enter a scan name, then add parameters manually or let AI draft them for you.'
                : 'Update the scan name below.'}
            </Typography>
          </Box>
          <IconButton
            onClick={handleCategoryDialogClose}
            size="small"
            sx={{ color: '#fff', '&:hover': { bgcolor: 'rgba(255,255,255,0.16)' } }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <DialogContent sx={{ p: 3, bgcolor: 'background.paper' }}>
          <Stack spacing={2.25} sx={{ mt: 0.5 }}>
            <Box>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.75 }}>
                Scan Name <Box component="span" sx={{ color: '#dc2626' }}>*</Box>
              </Typography>
              <TextField
                fullWidth
                size="small"
                value={categoryFormData.name}
                onChange={(e) => setCategoryFormData({ ...categoryFormData, name: e.target.value })}
                placeholder="e.g., Heart Scan, Abdominal Ultrasound, Chest X-Ray"
                required
                sx={{
                  '& .MuiOutlinedInput-root': { borderRadius: 1.75, bgcolor: 'background.paper' },
                  '& .MuiInputBase-input': { textTransform: 'uppercase' },
                }}
              />
            </Box>

            {/* Waiting time — days the patient stays on the scan panel.
                The panel keeps the patient visible for at least this many
                days and until results are entered, whichever takes longer. */}
            <Box>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.75 }}>
                Waiting Time (Days)
              </Typography>
              <TextField
                fullWidth
                size="small"
                type="number"
                inputProps={{ min: 0, step: 1 }}
                value={categoryFormData.waitingTime}
                onChange={(e) => setCategoryFormData({ ...categoryFormData, waitingTime: e.target.value })}
                placeholder="e.g., 1 for X-ray, 3 for MRI"
                helperText="Patient stays on the scan panel for at least this many days and until results are entered."
                sx={{
                  '& .MuiOutlinedInput-root': { borderRadius: 1.75, bgcolor: 'background.paper' },
                }}
              />
            </Box>

            {/* ── Report Format ─────────────────────────────────────
                Two shapes of scan supported:
                • Parameter — each parameter has its own value + flag,
                  rendered in the report as a table (default; every
                  existing scan uses this).
                • Narrative — each row is a section title with a
                  paragraph finding, plus a single clinical Impression
                  at the bottom. Report renders in the descriptive
                  layout (like a peripheral blood smear). */}
            <Box sx={{
              p: 1.5, borderRadius: 2,
              border: '1.5px solid',
              borderColor: categoryFormData.scanFormat === 'narrative' ? 'rgba(180,83,9,0.40)' : 'divider',
              background: categoryFormData.scanFormat === 'narrative'
                ? 'linear-gradient(135deg, rgba(180,83,9,0.04) 0%, rgba(217,119,6,0.04) 100%)'
                : 'transparent',
            }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.5 }}>
                    Report Format
                  </Typography>
                  <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', lineHeight: 1.4 }}>
                    {categoryFormData.scanFormat === 'narrative'
                      ? 'Each parameter row becomes a section title; the tech enters paragraph findings + a clinical Impression at the bottom. The report renders the descriptive layout.'
                      : 'Each parameter has a value + flag; the report renders as a parameter table. The default for most scans.'}
                  </Typography>
                </Box>
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <Select
                    value={categoryFormData.scanFormat}
                    onChange={(e) => setCategoryFormData({ ...categoryFormData, scanFormat: e.target.value })}
                    sx={{ fontWeight: 700, borderRadius: 1.75, bgcolor: 'background.paper' }}
                  >
                    <MenuItem value="parameter">Parameter (values)</MenuItem>
                    <MenuItem value="narrative">Narrative (descriptive)</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Box>

            {dialogMode === 'add' && (
              <>
                {/* AI generate button — pre-fills the parameter rows below
                    so the user can review/edit before saving the scan. */}
                <Box sx={{
                  display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap',
                  p: 1.5, borderRadius: 2,
                  bgcolor: 'rgba(124,58,237,0.06)',
                  border: '1px dashed rgba(124,58,237,0.35)',
                }}>
                  <Box sx={{
                    width: 36, height: 36, borderRadius: 1.5,
                    background: 'linear-gradient(135deg, #7c3aed 0%, #ec4899 100%)',
                    color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    boxShadow: '0 3px 10px rgba(124,58,237,0.32)', flexShrink: 0,
                  }}>
                    <AutoAwesomeIcon sx={{ fontSize: 18 }} />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 180 }}>
                    <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: 'text.primary', lineHeight: 1.25 }}>
                      Let AI draft the parameters
                    </Typography>
                    <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', lineHeight: 1.3 }}>
                      Every structure and its findings will load into the form below.
                    </Typography>
                  </Box>
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<AutoAwesomeIcon sx={{ fontSize: 16 }} />}
                    onClick={() => handleOpenAiDialog(true)}
                    sx={{
                      textTransform: 'none', fontWeight: 700, borderRadius: 1.5,
                      background: 'linear-gradient(135deg, #7c3aed 0%, #ec4899 100%)',
                      boxShadow: '0 3px 10px rgba(124,58,237,0.28)', whiteSpace: 'nowrap',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #6d28d9 0%, #db2777 100%)',
                        boxShadow: '0 5px 14px rgba(124,58,237,0.4)',
                      },
                    }}
                  >
                    Generate with AI
                  </Button>
                </Box>

                <Divider sx={{ my: 0.5 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase' }}>
                    Parameters
                  </Typography>
                </Divider>

                <Box>
                  {parametersList.map((param, index) => (
                    <Paper
                      key={param.id}
                      elevation={0}
                      sx={{
                        mb: 1.5, p: 1.75,
                        border: '1px solid', borderColor: 'divider', borderRadius: 2,
                        bgcolor: 'background.paper',
                        '&:hover': { borderColor: 'rgba(26,155,127,0.4)' },
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.25 }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          {/* Order badge — always the row's real position,
                              which the arrows keep in step with the "#"
                              input and with what gets saved. */}
                          <Box sx={{
                            width: 26, height: 26, borderRadius: '50%',
                            bgcolor: 'rgba(26,155,127,0.12)', color: '#1A9B7F',
                            fontWeight: 700, fontSize: '0.78rem',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                          }}>
                            {index + 1}
                          </Box>
                          <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: 'text.primary' }}>
                            Parameter
                          </Typography>
                          <TextField
                            size="small"
                            type="number"
                            value={param.displayOrder ?? ''}
                            onChange={(e) => handleParameterChange(param.id, 'displayOrder', e.target.value)}
                            onBlur={() => commitParameterOrder(param.id)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                commitParameterOrder(param.id);
                              }
                            }}
                            inputProps={{ min: 1, max: parametersList.length, step: 1, style: { textAlign: 'center', padding: '3px 4px', width: 40 } }}
                            placeholder="#"
                            sx={{
                              ml: 0.25,
                              '& .MuiOutlinedInput-root': { borderRadius: 1.25, bgcolor: 'background.paper' },
                            }}
                          />
                          <Box sx={{ display: 'flex', alignItems: 'center', ml: 0.5 }}>
                            <Tooltip title={index === 0 ? 'Already at the top' : 'Move up'}>
                              <span>
                                <IconButton
                                  size="small"
                                  disabled={index === 0}
                                  onClick={() => moveScanParameterUp(param.id)}
                                  sx={{
                                    width: 24, height: 24,
                                    color: index === 0 ? 'text.disabled' : '#1A9B7F',
                                    '&:hover': { bgcolor: 'rgba(26,155,127,0.10)' },
                                  }}
                                >
                                  <ArrowUpwardIcon sx={{ fontSize: 15 }} />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title={index === parametersList.length - 1 ? 'Already at the bottom' : 'Move down'}>
                              <span>
                                <IconButton
                                  size="small"
                                  disabled={index === parametersList.length - 1}
                                  onClick={() => moveScanParameterDown(param.id)}
                                  sx={{
                                    width: 24, height: 24,
                                    color: index === parametersList.length - 1 ? 'text.disabled' : '#1A9B7F',
                                    '&:hover': { bgcolor: 'rgba(26,155,127,0.10)' },
                                  }}
                                >
                                  <ArrowDownwardIcon sx={{ fontSize: 15 }} />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Box>
                        </Stack>
                        {parametersList.length > 1 && (
                          <Tooltip title="Remove this parameter" arrow>
                            <IconButton
                              size="small"
                              onClick={() => handleRemoveParameterField(param.id)}
                              sx={{ color: '#dc2626', '&:hover': { bgcolor: 'rgba(220,38,38,0.08)' } }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                      <Box sx={{
                        display: 'flex',
                        gap: 1.5,
                        flexDirection: { xs: 'column', sm: 'row' },
                        width: '100%',
                      }}>
                        <TextField
                          fullWidth size="small"
                          label="Parameter Name"
                          value={param.name}
                          onChange={(e) => handleParameterChange(param.id, 'name', e.target.value)}
                          placeholder="e.g., Liver, Right kidney"
                          sx={{ flex: { sm: 1 }, '& .MuiOutlinedInput-root': { borderRadius: 1.75 } }}
                        />
                      </Box>

                      {/* Value type toggle (order lives on the header). */}
                      <Box sx={{
                        display: 'flex', gap: 1.5,
                        flexDirection: { xs: 'column', sm: 'row' },
                        width: '100%', mt: 1.25,
                      }}>
                        <FormControl size="small" sx={{ width: { sm: 200 } }}>
                          <InputLabel>Value Type</InputLabel>
                          <Select
                            label="Value Type"
                            value={param.valueType || 'text'}
                            onChange={(e) => handleParameterChange(param.id, 'valueType', e.target.value)}
                            sx={{ borderRadius: 1.75 }}
                          >
                            <MenuItem value="text">Text (narrative)</MenuItem>
                            <MenuItem value="numeric">Numeric (with range)</MenuItem>
                          </Select>
                        </FormControl>
                      </Box>

                      {/* Default Finding — expanded to a multi-row
                          textarea so the admin has real room to type
                          long narratives per parameter. */}
                      <Box sx={{ mt: 1.5 }}>
                        <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.4px', mb: 0.5 }}>
                          Default Finding (pre-loaded narrative)
                        </Typography>
                        <TextField
                          fullWidth
                          multiline
                          minRows={4}
                          maxRows={12}
                          value={param.defaultFinding || ''}
                          onChange={(e) => handleParameterChange(param.id, 'defaultFinding', e.target.value)}
                          placeholder="Pre-fills the result text box when a tech opens this parameter at result entry."
                          sx={{
                            width: '100%',
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 1.75,
                              fontSize: '0.9rem',
                              lineHeight: 1.55,
                            },
                          }}
                        />
                      </Box>

                      {/* Unit + reference range — only meaningful for
                          numeric parameters. Disabled for text rows so
                          the lab admin sees they don't apply. */}
                      {param.valueType === 'numeric' && (
                        <Box sx={{
                          display: 'flex', gap: 1.5,
                          flexDirection: { xs: 'column', sm: 'row' },
                          width: '100%', mt: 1.25,
                          p: 1.25, borderRadius: 1.5,
                          bgcolor: 'rgba(96,165,250,0.06)',
                          border: '1px dashed rgba(96,165,250,0.30)',
                        }}>
                          <TextField
                            fullWidth size="small"
                            label="Unit"
                            value={param.unit || ''}
                            onChange={(e) => handleParameterChange(param.id, 'unit', e.target.value)}
                            placeholder="e.g., kg, weeks, mm, ms"
                            sx={{ flex: 1, '& .MuiOutlinedInput-root': { borderRadius: 1.75 } }}
                          />
                          <TextField
                            fullWidth size="small"
                            label="Reference Range"
                            value={param.refRange || ''}
                            onChange={(e) => handleParameterChange(param.id, 'refRange', e.target.value)}
                            placeholder="e.g., 2.5 - 4.0, < 50, ≥ 60, Negative"
                            helperText="Drives auto-flag + the scale chart on entry."
                            sx={{ flex: 1.4, '& .MuiOutlinedInput-root': { borderRadius: 1.75 } }}
                          />
                        </Box>
                      )}
                    </Paper>
                  ))}

                  <Box sx={{ textAlign: 'center', mt: 1 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={handleAddParameterField}
                      sx={{
                        textTransform: 'none', fontWeight: 600, borderRadius: 1.75,
                        borderStyle: 'dashed', borderColor: 'rgba(26,155,127,0.5)',
                        color: '#1A9B7F', px: 2.25,
                        '&:hover': { borderStyle: 'dashed', borderColor: '#1A9B7F', bgcolor: 'rgba(26,155,127,0.06)' },
                      }}
                    >
                      Add Another Parameter
                    </Button>
                  </Box>
                </Box>
              </>
            )}
          </Stack>
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            py: 2,
            bgcolor: 'action.hover',
            borderTop: '1px solid #e5e9ef',
            gap: 1,
          }}
        >
          <Button
            onClick={handleCategoryDialogClose}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              color: 'text.secondary',
              px: 2.5,
              '&:hover': { bgcolor: '#e2e8f0' },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveCategory}
            variant="contained"
            disabled={loading || !categoryFormData.name}
            startIcon={loading ? <RefreshIcon /> : <SaveIcon />}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              px: 3,
              borderRadius: 1.75,
              boxShadow: 'none',
              background: 'linear-gradient(135deg, #1A9B7F 0%, #157763 100%)',
              '&:hover': {
                boxShadow: '0 4px 12px rgba(26, 155, 127, 0.3)',
                background: 'linear-gradient(135deg, #157763 0%, #126553 100%)',
              },
            }}
          >
            {loading
              ? 'Saving…'
              : dialogMode === 'add'
                ? (() => {
                    const n = parametersList.filter(p => p.name.trim() !== '').length;
                    return n > 0 ? `Create Scan + ${n} Parameter${n === 1 ? '' : 's'}` : 'Create Scan';
                  })()
                : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add/Edit Parameter Dialog — sized wide + tall on purpose so
          there's real room for long default findings and long parameter
          lists. Wider than the other dialogs on the page since scan
          narratives can run to several paragraphs per parameter. */}
      <Dialog
        open={isParameterDialogOpen}
        onClose={handleParameterDialogClose}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden',
            boxShadow: '0 20px 60px rgba(15, 23, 42, 0.18)',
            height: '94vh',
            width: '96vw',
            maxWidth: '1400px',
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            px: 3,
            py: 2.25,
            background: 'linear-gradient(135deg, #1A9B7F 0%, #157763 100%)',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
          }}
        >
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'rgba(255,255,255,0.18)',
              backdropFilter: 'blur(8px)',
              flexShrink: 0,
            }}
          >
            <TuneIcon sx={{ fontSize: 22 }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: '1.05rem', fontWeight: 700, lineHeight: 1.2 }}>
              {dialogMode === 'manage'
                ? `Parameters for ${selectedCategory?.name || 'Scan'}`
                : dialogMode === 'add' ? 'Add Parameters' : 'Edit Parameter'}
            </Typography>
            <Typography sx={{ fontSize: '0.78rem', opacity: 0.88, mt: 0.25 }}>
              {dialogMode === 'manage'
                ? 'Add, edit, remove or AI-generate the parameters for this scan, then save all at once.'
                : dialogMode === 'add'
                  ? 'Define parameters and the flags that can be selected during result entry.'
                  : 'Update the parameter name and its available flags.'}
            </Typography>
          </Box>
          <IconButton
            onClick={handleParameterDialogClose}
            size="small"
            sx={{ color: '#fff', '&:hover': { bgcolor: 'rgba(255,255,255,0.16)' } }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <DialogContent sx={{ p: 3, bgcolor: 'action.hover' }}>
          {/* Info banner */}
          <Box
            sx={{
              mb: 2.5,
              p: 1.5,
              borderRadius: 2,
              bgcolor: 'rgba(26,155,127,0.08)',
              border: '1px solid rgba(26,155,127,0.2)',
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1.25,
            }}
          >
            <BiotechIcon sx={{ fontSize: 18, color: '#1A9B7F', mt: '2px' }} />
            <Typography sx={{ fontSize: '0.8rem', color: 'text.primary', lineHeight: 1.5 }}>
              {dialogMode === 'manage'
                ? 'Edit any row, add new ones below, or use the AI to append more — then save everything at once. Flags are comma-separated, e.g. '
                : dialogMode === 'add'
                  ? 'For each parameter, list the flags or findings that should be available when entering results — e.g. '
                  : 'Adjust the parameter name and the comma-separated flags that should be available — e.g. '}
              <Box component="span" sx={{ fontWeight: 700, color: '#1A9B7F' }}>normal, abnormal, high, low</Box>.
            </Typography>
          </Box>

          {(dialogMode === 'add' || dialogMode === 'manage') && (
            <Box sx={{ mb: 2.5, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                startIcon={<AutoAwesomeIcon sx={{ fontSize: 18 }} />}
                onClick={() => handleOpenAiDialog(true)}
                sx={{
                  textTransform: 'none',
                  fontWeight: 700,
                  borderRadius: 1.75,
                  px: 2.25,
                  boxShadow: '0 4px 12px rgba(124,58,237,0.28)',
                  background: 'linear-gradient(135deg, #7c3aed 0%, #ec4899 100%)',
                  '&:hover': {
                    boxShadow: '0 6px 16px rgba(124,58,237,0.40)',
                    background: 'linear-gradient(135deg, #6d28d9 0%, #db2777 100%)',
                  },
                }}
              >
                Generate with AI
              </Button>
            </Box>
          )}

          {parametersList.map((param, index) => (
            <Paper
              key={param.id}
              elevation={0}
              sx={{
                mb: 2,
                p: 2,
                border: '1px solid', borderColor: 'divider',
                borderRadius: 2.5,
                bgcolor: 'background.paper',
                transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
                '&:hover': {
                  borderColor: 'rgba(26,155,127,0.4)',
                  boxShadow: '0 2px 8px rgba(15, 23, 42, 0.04)',
                },
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.75 }}>
                <Stack direction="row" spacing={1.25} alignItems="center">
                  {/* Badge shows the row's real position. The arrows move
                      the row and renumber the whole list, so this, the "#"
                      input and the saved display_order always agree. */}
                  <Box
                    sx={{
                      width: 30,
                      height: 30,
                      borderRadius: '50%',
                      bgcolor: 'rgba(26,155,127,0.12)',
                      color: '#1A9B7F',
                      fontWeight: 700,
                      fontSize: '0.82rem',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {index + 1}
                  </Box>
                  <Typography sx={{ fontSize: '0.92rem', fontWeight: 700, color: 'text.primary' }}>
                    Parameter
                  </Typography>
                  {/* Order controls — the arrows swap the row with its
                      neighbour and renumber the list on the spot; typing a
                      number and leaving the field jumps the row straight
                      to that position. Either way the list is 1..n before
                      Save All runs. */}
                  <TextField
                    size="small"
                    type="number"
                    value={param.displayOrder ?? ''}
                    onChange={(e) => handleParameterChange(param.id, 'displayOrder', e.target.value)}
                    onBlur={() => commitParameterOrder(param.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        commitParameterOrder(param.id);
                      }
                    }}
                    inputProps={{ min: 1, max: parametersList.length, step: 1, style: { textAlign: 'center', padding: '4px 6px', width: 44 } }}
                    placeholder="#"
                    sx={{
                      ml: 0.5,
                      '& .MuiOutlinedInput-root': { borderRadius: 1.25, bgcolor: 'background.paper' },
                    }}
                  />
                  <Box sx={{ display: 'flex', alignItems: 'center', ml: 0.5 }}>
                    <Tooltip title={index === 0 ? 'Already at the top' : 'Move up'}>
                      <span>
                        <IconButton
                          size="small"
                          disabled={index === 0}
                          onClick={() => moveScanParameterUp(param.id)}
                          sx={{
                            width: 26, height: 26,
                            color: index === 0 ? 'text.disabled' : '#1A9B7F',
                            '&:hover': { bgcolor: 'rgba(26,155,127,0.10)' },
                          }}
                        >
                          <ArrowUpwardIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title={index === parametersList.length - 1 ? 'Already at the bottom' : 'Move down'}>
                      <span>
                        <IconButton
                          size="small"
                          disabled={index === parametersList.length - 1}
                          onClick={() => moveScanParameterDown(param.id)}
                          sx={{
                            width: 26, height: 26,
                            color: index === parametersList.length - 1 ? 'text.disabled' : '#1A9B7F',
                            '&:hover': { bgcolor: 'rgba(26,155,127,0.10)' },
                          }}
                        >
                          <ArrowDownwardIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                </Stack>
                {(parametersList.length > 1 || dialogMode === 'manage') && (
                  <Tooltip title="Remove this parameter" arrow>
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveParameterField(param.id)}
                      sx={{
                        color: '#dc2626',
                        '&:hover': { bgcolor: 'rgba(220,38,38,0.08)' },
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>

              <Box sx={{
                display: 'flex',
                gap: 2,
                flexDirection: { xs: 'column', sm: 'row' },
                width: '100%',
              }}>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.75 }}>
                    Parameter Name <Box component="span" sx={{ color: '#dc2626' }}>*</Box>
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    value={param.name}
                    onChange={(e) => handleParameterChange(param.id, 'name', e.target.value)}
                    placeholder="e.g., LIVER"
                    required
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.75, bgcolor: 'background.paper' } }}
                  />
                </Box>
              </Box>

              {/* Metadata row — value type. Order lives on the header
                  above (manual number + arrows). */}
              <Box sx={{
                display: 'flex', gap: 2,
                flexDirection: { xs: 'column', sm: 'row' },
                width: '100%', mt: 1.75,
              }}>
                <FormControl size="small" sx={{ width: { sm: 220 } }}>
                  <InputLabel>Value Type</InputLabel>
                  <Select
                    label="Value Type"
                    value={param.valueType || 'text'}
                    onChange={(e) => handleParameterChange(param.id, 'valueType', e.target.value)}
                    sx={{ borderRadius: 1.75, bgcolor: 'background.paper' }}
                  >
                    <MenuItem value="text">Text (narrative)</MenuItem>
                    <MenuItem value="numeric">Numeric (with range)</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              {/* Default Finding — the pre-loaded narrative the tech
                  sees at result entry. Made big on purpose: scan
                  narratives can run for paragraphs (e.g. a full liver
                  description), and cramping this into one line was
                  the whole complaint that motivated widening the dialog. */}
              <Box sx={{ mt: 1.75 }}>
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.75 }}>
                  Default Finding (pre-loaded narrative)
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  minRows={5}
                  maxRows={14}
                  value={param.defaultFinding || ''}
                  onChange={(e) => handleParameterChange(param.id, 'defaultFinding', e.target.value)}
                  placeholder={`Pre-fills the result text box when a tech opens this parameter at result entry.\nExample for LIVER:\n"Normal in size, outline and echotexture. No focal lesion. Portal and hepatic vasculature are unremarkable. No intra- or extra-hepatic biliary duct dilatation."`}
                  helperText="The technician sees this text pre-loaded, edits what changes, and saves. Leave blank if this parameter has no useful default."
                  sx={{
                    width: '100%',
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1.75,
                      bgcolor: 'background.paper',
                      fontSize: '0.92rem',
                      lineHeight: 1.55,
                    },
                  }}
                />
              </Box>

              {/* Unit + ref range — only meaningful for numeric rows. */}
              {param.valueType === 'numeric' && (
                <Box sx={{
                  display: 'flex', gap: 2,
                  flexDirection: { xs: 'column', sm: 'row' },
                  width: '100%', mt: 1.5,
                  p: 1.5, borderRadius: 1.5,
                  bgcolor: 'rgba(96,165,250,0.06)',
                  border: '1px dashed rgba(96,165,250,0.30)',
                }}>
                  <TextField
                    fullWidth size="small"
                    label="Unit"
                    value={param.unit || ''}
                    onChange={(e) => handleParameterChange(param.id, 'unit', e.target.value)}
                    placeholder="e.g., kg, weeks, mm, ms"
                    sx={{ flex: 1, '& .MuiOutlinedInput-root': { borderRadius: 1.75, bgcolor: 'background.paper' } }}
                  />
                  <TextField
                    fullWidth size="small"
                    label="Reference Range"
                    value={param.refRange || ''}
                    onChange={(e) => handleParameterChange(param.id, 'refRange', e.target.value)}
                    placeholder="e.g., 2.5 - 4.0, < 50, ≥ 60, Negative"
                    helperText="Drives auto-flag + the scale chart on entry."
                    sx={{ flex: 1.4, '& .MuiOutlinedInput-root': { borderRadius: 1.75, bgcolor: 'background.paper' } }}
                  />
                </Box>
              )}
            </Paper>
          ))}

          {(dialogMode === 'add' || dialogMode === 'manage') && (
            <Box sx={{ textAlign: 'center', mt: 1.5 }}>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={handleAddParameterField}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: 1.75,
                  borderStyle: 'dashed',
                  borderColor: 'rgba(26,155,127,0.5)',
                  color: '#1A9B7F',
                  px: 2.5,
                  '&:hover': {
                    borderStyle: 'dashed',
                    borderColor: '#1A9B7F',
                    bgcolor: 'rgba(26,155,127,0.06)',
                  },
                }}
              >
                Add Another Parameter
              </Button>
            </Box>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            py: 2,
            bgcolor: 'background.paper',
            borderTop: '1px solid #e5e9ef',
            gap: 1,
          }}
        >
          {(() => {
            const validCount = parametersList.filter(p => p.name.trim() !== '').length;
            return (
              <>
                <Typography sx={{ flex: 1, fontSize: '0.78rem', color: 'text.secondary' }}>
                  {validCount > 0
                    ? `${validCount} parameter${validCount === 1 ? '' : 's'} ready to save`
                    : 'Enter at least one parameter name to save'}
                </Typography>
                {/* Preview — uses the live parametersList so the admin
                    can eye-check the ordering + pre-filled findings
                    without saving first. */}
                <Button
                  onClick={handleOpenScanPreviewFromDialog}
                  startIcon={<VisibilityIcon sx={{ fontSize: 18 }} />}
                  disabled={validCount === 0}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 700,
                    color: '#5468FF',
                    px: 2,
                    borderRadius: 1.75,
                    '&:hover': { bgcolor: 'rgba(84,104,255,0.08)' },
                  }}
                >
                  Preview
                </Button>
                <Button
                  onClick={handleParameterDialogClose}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    color: 'text.secondary',
                    px: 2.5,
                    '&:hover': { bgcolor: '#e2e8f0' },
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveParameter}
                  variant="contained"
                  disabled={loading || validCount === 0}
                  startIcon={loading ? <RefreshIcon /> : <SaveIcon />}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 700,
                    px: 3,
                    borderRadius: 1.75,
                    boxShadow: 'none',
                    background: 'linear-gradient(135deg, #1A9B7F 0%, #157763 100%)',
                    '&:hover': {
                      boxShadow: '0 4px 12px rgba(26, 155, 127, 0.3)',
                      background: 'linear-gradient(135deg, #157763 0%, #126553 100%)',
                    },
                  }}
                >
                  {loading
                    ? 'Saving…'
                    : dialogMode === 'manage'
                      ? `Save All (${validCount})`
                      : dialogMode === 'add'
                        ? `Save ${validCount} Parameter${validCount === 1 ? '' : 's'}`
                        : 'Save Changes'}
                </Button>
              </>
            );
          })()}
        </DialogActions>
      </Dialog>

      {/* AI Generate Parameters Dialog */}
      <Dialog
        open={aiDialogOpen}
        onClose={handleCloseAiDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden', boxShadow: '0 20px 60px rgba(15,23,42,0.18)' } }}
      >
        <Box
          sx={{
            px: 3, py: 2.25,
            background: 'linear-gradient(135deg, #7c3aed 0%, #ec4899 100%)',
            color: '#fff', display: 'flex', alignItems: 'center', gap: 1.5,
          }}
        >
          <Box sx={{
            width: 44, height: 44, borderRadius: 2,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            bgcolor: 'rgba(255,255,255,0.18)', backdropFilter: 'blur(8px)', flexShrink: 0,
          }}>
            <AutoAwesomeIcon sx={{ fontSize: 22 }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: '1.05rem', fontWeight: 700, lineHeight: 1.2 }}>
              AI Scan Parameter Generator
            </Typography>
            <Typography sx={{ fontSize: '0.78rem', opacity: 0.88, mt: 0.25 }}>
              Type the scan name — the AI drafts every anatomical structure and its flags.
            </Typography>
          </Box>
          <IconButton
            onClick={handleCloseAiDialog}
            size="small"
            disabled={aiGenerating}
            sx={{ color: '#fff', '&:hover': { bgcolor: 'rgba(255,255,255,0.16)' } }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <DialogContent sx={{ p: 3, bgcolor: 'background.paper', position: 'relative' }}>
          {/* Full-content loading overlay while the AI generates — mirrors
              the lab's loadingLabGroup pattern so the user gets a strong
              signal that work is in progress. */}
          {aiGenerating && (
            <Box sx={{
              position: 'absolute', inset: 0, zIndex: 10,
              display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center', gap: 1.5,
              bgcolor: (theme) => theme.palette.mode === 'dark'
                ? 'rgba(0,0,0,0.65)'
                : 'rgba(255,255,255,0.78)',
              backdropFilter: 'blur(2px)',
            }}>
              <CircularProgress size={44} thickness={4} sx={{ color: '#7c3aed' }} />
              <Box sx={{ fontSize: '0.95rem', fontWeight: 700, color: 'text.primary' }}>
                Generating parameters…
              </Box>
              <Box sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>
                {aiForm.scanName ? `Drafting "${String(aiForm.scanName).toUpperCase()}"` : 'This usually takes a few seconds'}
              </Box>
            </Box>
          )}
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <Box>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.75 }}>
                Scan Name <Box component="span" sx={{ color: '#dc2626' }}>*</Box>
              </Typography>
              <TextField
                fullWidth size="small" autoFocus
                value={aiForm.scanName}
                onChange={(e) => setAiForm({ ...aiForm, scanName: e.target.value })}
                placeholder="e.g., Abdominal Ultrasound"
                sx={{
                  '& .MuiOutlinedInput-root': { borderRadius: 1.75 },
                  '& .MuiInputBase-input': { textTransform: 'uppercase' },
                }}
              />
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.75 }}>
                  Modality
                </Typography>
                <TextField
                  fullWidth size="small"
                  value={aiForm.modality}
                  onChange={(e) => setAiForm({ ...aiForm, modality: e.target.value })}
                  placeholder="e.g., Ultrasound, CT, MRI, X-Ray"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.75 } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.75 }}>
                  Body Region
                </Typography>
                <TextField
                  fullWidth size="small"
                  value={aiForm.bodyRegion}
                  onChange={(e) => setAiForm({ ...aiForm, bodyRegion: e.target.value })}
                  placeholder="e.g., Abdomen, Chest, Pelvis"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.75 } }}
                />
              </Grid>
            </Grid>
            <Box>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.75 }}>
                Region / Patient Population
              </Typography>
              <TextField
                fullWidth size="small"
                value={aiForm.region}
                onChange={(e) => setAiForm({ ...aiForm, region: e.target.value })}
                placeholder="Ghana / West Africa"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.75 } }}
              />
            </Box>
            <Box>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.75 }}>
                Extra Notes
              </Typography>
              <TextField
                fullWidth size="small" multiline rows={2}
                value={aiForm.extraNotes}
                onChange={(e) => setAiForm({ ...aiForm, extraNotes: e.target.value })}
                placeholder="Anything else the AI should know (protocol, contrast, indication)…"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.75 } }}
              />
            </Box>

            {aiError && (
              <Alert severity="error" sx={{ borderRadius: 1.75 }}>{aiError}</Alert>
            )}

            <Box sx={{
              p: 1.5, borderRadius: 2,
              bgcolor: 'rgba(124,58,237,0.06)',
              border: '1px solid rgba(124,58,237,0.20)',
              display: 'flex', gap: 1.25, alignItems: 'flex-start',
            }}>
              <AutoAwesomeIcon sx={{ fontSize: 18, color: '#7c3aed', mt: '2px' }} />
              <Typography sx={{ fontSize: '0.78rem', color: 'text.primary', lineHeight: 1.5 }}>
                The AI will draft every structure routinely reported for this scan
                (e.g. Liver, Right kidney, Spleen) and the comma-separated flags
                used during result entry. You can edit everything before saving.
              </Typography>
            </Box>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, bgcolor: 'action.hover', borderTop: '1px solid #e5e9ef', gap: 1 }}>
          <Button
            onClick={handleCloseAiDialog}
            disabled={aiGenerating}
            sx={{ textTransform: 'none', fontWeight: 600, color: 'text.secondary', px: 2.5 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleGenerateWithAi}
            variant="contained"
            disabled={aiGenerating || !aiForm.scanName.trim()}
            startIcon={aiGenerating ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <AutoAwesomeIcon />}
            sx={{
              textTransform: 'none', fontWeight: 700, px: 3, borderRadius: 1.75,
              boxShadow: 'none',
              background: 'linear-gradient(135deg, #7c3aed 0%, #ec4899 100%)',
              '&:hover': {
                boxShadow: '0 4px 12px rgba(124,58,237,0.30)',
                background: 'linear-gradient(135deg, #6d28d9 0%, #db2777 100%)',
              },
            }}
          >
            {aiGenerating ? 'Generating…' : 'Generate Parameters'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Scan pricing info dialog (mirror of Home.js) ────────────── */}
      <Dialog
        open={infoDialogOpen}
        onClose={() => setInfoDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 800, borderBottom: '1px solid', borderColor: 'divider' }}>
          <InfoIcon sx={{ color: '#0ea5e9' }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontWeight: 800, fontSize: '1rem' }}>Scan pricing details</Typography>
            <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary', mt: 0.25 }}>{infoScanName}</Typography>
          </Box>
          <IconButton onClick={() => setInfoDialogOpen(false)} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {infoLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress size={28} />
            </Box>
          ) : infoRows.length === 0 ? (
            <Box sx={{
              textAlign: 'center', py: 5, px: 2,
              border: '1px dashed', borderColor: 'divider', borderRadius: 2,
              bgcolor: 'rgba(245,158,11,0.06)',
            }}>
              <MoneyIcon sx={{ fontSize: 36, color: '#f59e0b', mb: 1 }} />
              <Typography sx={{ fontWeight: 800 }}>No price set yet</Typography>
              <Typography sx={{ fontSize: '0.82rem', color: 'text.secondary', mt: 0.5 }}>
                This scan has no pricing rows on file. Use "Request change" below to ask admin to set one.
              </Typography>
            </Box>
          ) : (
            <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Insurance</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align="right">Tariff</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align="right">Top-up</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>G-DRG</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align="center">Request</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {infoRows.map((r, i) => (
                    <TableRow key={i} hover>
                      <TableCell sx={{ fontWeight: 600 }}>{r.status || '—'}</TableCell>
                      <TableCell align="right" sx={{ fontFamily: 'monospace' }}>
                        {Number(r.tariff) ? Number(r.tariff).toFixed(2) : (
                          <Chip size="small" label="not set" sx={{ height: 18, fontSize: '0.62rem', bgcolor: 'rgba(245,158,11,0.18)', color: '#f59e0b', fontWeight: 700 }} />
                        )}
                      </TableCell>
                      <TableCell align="right" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
                        {Number(r.topup) ? Number(r.topup).toFixed(2) : '—'}
                      </TableCell>
                      <TableCell sx={{ color: 'text.secondary', fontSize: '0.78rem' }}>{r.GDRG || '—'}</TableCell>
                      <TableCell align="center">
                        <Tooltip title="Request a change to this price">
                          <IconButton size="small" onClick={() => openRequestChange(r)} sx={{ color: '#f59e0b' }}>
                            <PriceChangeIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider', gap: 1 }}>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: '0.72rem', color: 'text.disabled' }}>
              Read-only view — changes go through admin via the request form.
            </Typography>
          </Box>
          <Button
            onClick={() => openRequestChange(null)}
            startIcon={<PriceChangeIcon />}
            variant="contained"
            sx={{ textTransform: 'none', fontWeight: 700, bgcolor: '#f59e0b', '&:hover': { bgcolor: '#d97706' } }}
          >
            Request change
          </Button>
          <Button onClick={() => setInfoDialogOpen(false)} color="inherit" sx={{ textTransform: 'none' }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* ── Request scan price change dialog ────────────────────────── */}
      <Dialog
        open={reqOpen}
        onClose={() => { if (!reqSaving) setReqOpen(false); }}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ fontWeight: 800, borderBottom: '1px solid', borderColor: 'divider' }}>
          Request price change
        </DialogTitle>
        <DialogContent sx={{ pt: 2.5 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Box>
              <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                Scan
              </Typography>
              <Typography sx={{ fontSize: '0.92rem', fontWeight: 700, mt: 0.25 }}>{infoScanName}</Typography>
            </Box>
            <FormControl size="small" fullWidth>
              <InputLabel>Insurance partner</InputLabel>
              <Select
                label="Insurance partner"
                value={reqInsurance}
                onChange={(e) => {
                  const v = e.target.value;
                  setReqInsurance(v);
                  const row = infoRows.find((r) => (r.status || '') === v);
                  if (row) {
                    setReqCurrent(Number(row.tariff));
                    if (!reqAmount) setReqAmount(String(row.tariff ?? ''));
                  } else {
                    setReqCurrent(null);
                  }
                }}
              >
                <MenuItem value=""><em>— Uninsured / general —</em></MenuItem>
                {insurancePartners.map((ip) => (
                  <MenuItem key={ip.name || ip.id} value={ip.name}>{ip.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              size="small"
              fullWidth
              label="Current tariff on file"
              value={reqCurrent != null && Number.isFinite(Number(reqCurrent)) ? Number(reqCurrent).toFixed(2) : '—'}
              InputProps={{ readOnly: true }}
              sx={{ '& .MuiInputBase-input': { fontFamily: 'monospace' } }}
            />
            <TextField
              size="small"
              fullWidth
              type="number"
              label="Proposed new tariff *"
              value={reqAmount}
              onChange={(e) => setReqAmount(e.target.value)}
              inputProps={{ step: '0.01', min: '0' }}
            />
            <TextField
              size="small"
              fullWidth
              multiline
              minRows={2}
              maxRows={4}
              label="Reason (optional)"
              placeholder="e.g. modality upgrade, mismatch with current rate card…"
              value={reqReason}
              onChange={(e) => setReqReason(e.target.value)}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button onClick={() => setReqOpen(false)} disabled={reqSaving} color="inherit" sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button
            onClick={submitChangeRequest}
            disabled={reqSaving || !reqAmount}
            startIcon={reqSaving ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <PriceChangeIcon />}
            variant="contained"
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            Submit request
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Scan report preview dialog ─────────────────────────────────
          Renders a mock scan report from the current parameters (either
          the saved list off a category row, or the live parametersList
          inside the parameter editor). Uses the Dekyi branded template
          so what the admin sees here matches the printed output. */}
      <Dialog
        open={previewOpen}
        onClose={handleCloseScanPreview}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { height: '92vh' } }}
      >
        <DialogTitle sx={{ pb: 1, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <VisibilityIcon sx={{ color: '#5468FF' }} />
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontWeight: 700, fontSize: '1.05rem' }}>
              Report Preview — {previewCategory?.name || ''}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Sample values only. Not a real result. Use this to confirm the layout, parameter order, and pre-filled findings.
            </Typography>
          </Box>
          <Chip
            size="small"
            label="PREVIEW · SAMPLE DATA"
            sx={{
              fontWeight: 700, fontSize: '0.65rem', letterSpacing: '.6px',
              bgcolor: 'rgba(255,193,7,0.18)', color: '#a07408',
              border: '1px solid rgba(255,193,7,0.5)',
            }}
          />
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ flex: 1, bgcolor: '#f5f5f5' }}>
            <iframe
              title="Scan report preview"
              srcDoc={previewHtml}
              style={{ width: '100%', height: '100%', border: 'none', backgroundColor: 'white' }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.25 }}>
          <Button onClick={handleCloseScanPreview} color="inherit">Close</Button>
          <Button onClick={handleOpenScanPreviewInWindow} variant="outlined">
            Open in new window
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SetupScan;